import {
  interceptorNet,
  withQuery,
  replaceURL,
  interceptFilter,
  hasUrlParams,
  createResponseError,
  RegURL,
  forgeRequest,
  setHeaders,
  headerFormatRule,
  headerFormat,
} from "./common"
import { region, i18n, cache, env as appEnv, store } from "@platform/index"
import {
  loginRule,
  loginReqInterceptor,
  loginResInterceptor,
} from "./loginAuth"
import { globalInfo } from "../env/util"
import { CONSTANTS } from "../../utils/CONSTANTS"
import { loadingReqInterceptor, loadingResInterceptor, loadingRule } from "./loading"

const { migrateModeMap, buildCodeKey, cacheKeyMap } = CONSTANTS

type TRequest = {
  [key: string]: <T = any>(
    config?: NetConfig & { url: string }
  ) => Promise<NetResult<T>>
}

class _Request {
  private request: TRequest
  private migrateMode: string
  private migrateConfig: any
  globalConfig: NetConfig = {}
  interceptors: InitIntercept<NetRequest, NetResult<any>> = {
    before: [],
    after: [],
  }

  constructor(request: TRequest) {
    this.request = request
    this.interceptors.before.push(
      { rule: hasUrlParams, use: replaceURL },
      { rule: RegURL, use: setHeaders },
      { rule: loginRule, use: loginReqInterceptor },
      { rule: headerFormatRule, use: headerFormat },
    )
    this.interceptors.after.push(
      { rule: RegURL, use: createResponseError },
      { rule: loginRule, use: loginResInterceptor },
    )
  }
  /**
   * 初始化
   */
  config(
    config?: NetConfig,
    interceptor?: InitIntercept<NetRequest, NetResult<any>>
  ) {
    this.globalConfig = config || {}
    const { before = [], after = [] } = interceptor || {} // 初始化时处理用户传过来的拦截器
    this.interceptors.before.push(
      ...before,
    )
    this.interceptors.after.push(
      ...after,
    )
  }
  /**
   * 请求前处理参数、异常， 执行request intercept
   */
  async preSend<T = any>(config: NetRequest): Promise<NetResult<T>> {
    // 开始服务漂移时，讲请求地址替换到shad本地服务
    let migrateMode = ""
    if (typeof TinyAPI !== 'undefined' && cache.get(cacheKeyMap.supportMigrate) === "true") {
      migrateMode = this.migrateMode = TinyAPI?.migrate?.getMode()
    }
    if (this.migrateMode === migrateModeMap.offline) {
      this.getMigrateConfig()
      // config.url = this.switchMigrateUri(config)
    }

    const { headers = {}, url, data = {}, query = "", method = "GET" } = config
    if (typeof url !== "string") throw Error("invalid URL")
    try {
      let requestInterceptors = [
        ...this.interceptors.before,
        { rule: loadingRule, use: loadingReqInterceptor },
      ]

      requestInterceptors = await interceptFilter(requestInterceptors, config)
      requestInterceptors = [
        ...requestInterceptors,
        ...(config?.interceptorBefore || []),
      ]
      if (requestInterceptors?.length) {
        config = await interceptorNet(requestInterceptors, { ...config })
      }

      const _config: NetRequest = {
        ...this.globalConfig,
        headers: {
          ...(this.globalConfig?.headers || {}),
          ...headers,
        },
        ...config,
        method,
      }
      _config.url = withQuery(_config.url, query, method === "GET" ? data : "")
      return this.send({ method, ..._config })
      // return this.send({ method, migrateMode, ..._config })
    } catch (e) {
      return e
    }
  }

  private getMigrateConfig() {
    const migrateConfig = cache.get(cacheKeyMap.migrateConfig)
    if (migrateConfig) this.migrateConfig = migrateConfig
  }

  private switchMigrateUri(config: NetRequest) {
    const migrateConfig = this.migrateConfig
    if (!migrateConfig) return config.data.httpData.uri

    const serverConfig = migrateConfig.serverConfig || {}
    const { protocol, server, port } = serverConfig
    return (config.data.httpData.uri = config.data.httpData.uri.replace(
      /^https:\/\/([^\/]+)\//i,
      `${protocol}://${server}:${port}/`
    ))
  }

  /**
   * 请求数据， 执行intercept response
   */
  async send<T = any>(config: NetRequest): Promise<NetResult<T>> {
    const { method } = config
    const supportMigrate = cache.get(cacheKeyMap.supportMigrate) === "true"
    try {
      const response = await this.request[method]({ ...config })

      // 在支持服务漂移的情况下，当网络在线时，需要更新本地模型
      if (supportMigrate && this.migrateMode === migrateModeMap.online)
        this.upgradeMigrateMode(response)

      let responseInterceptors = [
        ...this.interceptors.after,
        { rule: loadingRule, use: loadingResInterceptor },
      ]
      responseInterceptors = await interceptFilter(responseInterceptors, {
        ...config,
      })
      responseInterceptors = [
        ...responseInterceptors,
        ...(config?.interceptorAfter || []),
      ]
      if (responseInterceptors?.length) {
        const result = await interceptorNet(
          responseInterceptors,
          response,
          config
        )
        return result
      }
      // if (response.headers.namesAndValues) {
      // }
      return response
    } catch (e) {
      return e
    }
  }

  private upgradeMigrateMode(response: any) {
    // const headersNamesAndValues = response.headers.namesAndValues.toString()
    // const buildCodeIndex = headersNamesAndValues.indexOf(buildCodeKey)
    const headersNamesAndValues = response.headers?.namesAndValues
    if (!headersNamesAndValues?.length) return
    const buildCodeIndex = headersNamesAndValues.indexOf(buildCodeKey)
    if (buildCodeIndex === -1) return

    const dependModelProjects = this.getDependModelProjects()
    if (!dependModelProjects.length) return

    const currentDependModelProject =
      this.getCurrentDependModelProject(dependModelProjects)
    // const buildCodeKeyLen = buildCodeKey.length
    // const buildCodeValue = headersNamesAndValues.substr(
    //   buildCodeIndex + buildCodeKeyLen + 1,
    //   34
    // )
    const buildCodeValue = headersNamesAndValues?.[buildCodeIndex + 1]
    if (TinyAPI) {
      TinyAPI.migrate.upgradeMode(
        Object.assign(currentDependModelProject, {
          buildCode: buildCodeValue,
        })
      )
    }

  }

  private getDependModelProjects() {
    const migrateConfig = (cache.get(cacheKeyMap.migrateConfig) as any) || {}
    return migrateConfig.dependModelProjects || []
  }

  private getCurrentDependModelProject(dependModelProjects: any) {
    const tenantCode = cache.get(cacheKeyMap.tenantCode)
    return dependModelProjects.find((item: any) => {
      return item.tenantCode === tenantCode
    })
  }

  /**
   * 网关请求
   * @param serviceCode
   * @param _params
   * @param isGetway
   * @returns
   */
  async getway(
    services: {
      method?: string
      params?: unknown
      protocol?: string
      id?: string
    },
    serviceCode: string,
    _params?: { pathParams?: {}; body?: {}; header?: {} },
    args?: any,
    migrateConfig?: any
  ) {
    if (!services) throw Error(serviceCode + "not found")

    const { method, params, protocol, id: serviceId } = services || {}
    const tempMethod = method?.toLocaleLowerCase()
    let tempData: any = {}
    let {
      env,
      // tempEnv = "prod", // 平台env
      getwayUrl = "https://amoeba.sunmi.com",
      isGetway,
      getAll,
      serviceCallEnv,
      requestTimeout = "",
      timeout,
      useLoading,
      // appCode = "testApp",
    } = args
    // 区分平台环境
    getwayUrl = ["dev", "test", "uat"].includes(serviceCallEnv)
      ? "https://amoeba.dev.sunmi.com"
      : "https://amoeba.sunmi.com"

    const regionServerSupportedMap = region.getMap()
    const _region = region.get()
    if (globalInfo.get("regionServerSupported")) {
      getwayUrl = globalInfo.get("regionServerSupported")
    } else if (
      regionServerSupportedMap &&
      region &&
      regionServerSupportedMap[_region]
    ) {
      getwayUrl = regionServerSupportedMap[_region]
    }
    // 用户未传入 API 环境，兼容旧代码，默认使用prod
    const _env = env || "prod"
    const obj: { [key: string]: any } = {}
    for (const [key, value] of Object.entries(params)) {
      obj[key?.toLocaleLowerCase()] = value
    }
    tempData = obj[_env] || obj[_env?.toLocaleLowerCase()]
    if (!tempData) {
      throw Error(
        `could not find ${_env} from ${JSON.stringify(Object.keys(obj))}`
      )
    }
    let tempPath = ""
    if (tempData?.path) {
      tempPath =
        tempData.path.indexOf("/") === 0 ? tempData.path : "/" + tempData.path
    }
    const httpData = forgeRequest(
      tempData?.host + tempPath,
      tempMethod,
      tempData,
      _params
    )
    let result = null
    if (isGetway !== false) {
      // 网关请求
      try {
        // 网关请求增加sn和版本信息
        try {
          if (appEnv?.softVersion) {
            httpData.header["x-appVersion"] = appEnv?.softVersion
          }
          if (appEnv?.appVersionCode) {
            httpData.header["x-appVersionCode"] = appEnv?.appVersionCode
          }
          if (appEnv?.intranetIp) {
            httpData.header["x-intranetIp"] = appEnv?.intranetIp
          }
          const __sn = cache.get("__sn__")
          if (__sn) {
            httpData.header["x-device"] = __sn
            httpData.header["max-device-id"] = __sn
          }
          const _store = store.get()
          if (_store?.['store']) {
            httpData.header["max-store-id"] = _store['store']
          }
          httpData.header["max-language"] = i18n.getCurrentLang()

        } catch (e) {
          console.error("set sn error" + e)
        }
        const getWayRequestTimeout = requestTimeout?.length
          ? requestTimeout
          : tempData?.timeout

        result = await this.preSend({
          url: getwayUrl,
          data: {
            config: {
              requestTimeout: getWayRequestTimeout,
              serviceName: serviceCode,
            },
            httpData,
          },
          headers: {
            "content-type": "application/json",
            "g-model": "dt",
            "locale-language": i18n.getCurrentLang(),
            // 新网关改造 Start=====>
            "max-service-id": serviceId,
            "max-service-name": serviceCode,
            "max-proxy-timeout": getWayRequestTimeout,
            // "max-proxy-host"
            // "max-account-token": "x-token"
            // "max-timezone": "x-timezone"
            "max-appversion": appEnv?.softVersion,
            "max-appversioncode": appEnv?.appVersionCode,
            // 新网关改造 End=====>            
            // 流量监控 Start=====>
            // https://alidocs.dingtalk.com/i/nodes/R1zknDm0WR3Aqr7LUZAz3NaEVBQEx5rG?utm_source=dingdoc_doc&utm_scene=team_space&iframeQuery=utm_medium%3Dim_card%26utm_source%3Dim&utm_medium=dingdoc_doc_plugin_url&corpId=dingcd49b2ef062e15cd
            "a": { 'android': '1', 'web': '3' }[__platform] || '0', // 平台类型
            "c": _env, // 网络请求环境
            "d": __platform === 'preview' ? '1' : '0', // 平台流量类型
            "e": '3', // 普通流量
            // 流量监控 End=====>
          },
          method: "POST",
          unique: serviceId,
          env: _env,
          timeout: timeout,
          useLoading,
        })
      } catch (e) {
        return e
      }
    } else {
      const { uri, method, body, header } = httpData
      try {
        result = await this.preSend({
          url: uri,
          data: body,
          method: method?.toUpperCase(),
          headers: header,
          unique: serviceId,
          env: _env,
        })
      } catch (e) {
        return e
      }
    }
    if (getAll) return result
    return result?.body
  }
}
export default _Request
